import { ReviewIngredient } from "../../../../types/reviewTypes";

/**
 * The write side of a review flow.
 *
 * The review UI (ingredient rows, swap panel, bulk-apply panel) is identical for
 * every automation, but WHERE an edit lands is not: a cost-price review writes
 * cost price analyses, a subrecipe review writes subrecipes. Each flow supplies
 * an adapter; the components stay domain-agnostic.
 *
 * Every method must also patch its own review report so the card reflects the
 * edit without a refetch — that is why the store update lives in the adapter
 * rather than in the component.
 */

/** A swap target: an order-history product, a custom ingredient, or a subrecipe. */
export interface SwapCandidate {
    kind: 'product' | 'custom' | 'subrecipe';
    key: string;
    displayName: string;
    // product / custom
    articleId?: string;
    platform?: string;
    customPlatformName?: string;
    quantityInfo?: {
        stdAmount: number;
        stdMultiplier: number;
        stdUnit: string;
    };
    price?: number;
    // subrecipe — carries no quantityInfo; its quantity is expressed in portions.
    subrecipeId?: string;
    portionsUnit?: string;
    // Display only — never written to the analysis row.
    unitPriceLabel?: string;
    categoryLabel?: string;
}

/** The same template ingredient found on another item, offered for a bulk apply. */
export interface MatchingIngredientRef {
    /** Cost price id or subrecipe id, depending on the flow. */
    itemId: string;
    itemName: string;
    itemSubLabel?: string;
    ingredientIndex: number;
    ingredient: ReviewIngredient;
    /** What that item currently points at (product or subrecipe name), for display. */
    currentMatchName?: string;
    /** Quantity we would write there — that item's own template quantity in the
     *  candidate's unit, falling back to the quantity just used here. */
    quantity: number;
    /** A unit change requires the reviewer to enter this dish's new amount. */
    requiresQuantity?: boolean;
}

export interface ReviewWriteAdapter {
    /** Drop the row this report ingredient created, then mark it removed in the report. */
    remove(itemId: string, ingredient: ReviewIngredient, ingredientIndex: number): Promise<void>;
    /** Change only the quantity of an already-matched row, keeping its identity. */
    setQuantity(itemId: string, ingredient: ReviewIngredient, ingredientIndex: number, quantity: number): Promise<void>;
    /** Point this report ingredient at `candidate` instead, at `quantity`. */
    swap(
        itemId: string,
        ingredient: ReviewIngredient,
        ingredientIndex: number,
        candidate: SwapCandidate,
        quantity: number
    ): Promise<void>;
    /** Every OTHER item in the report carrying the same template ingredient name. */
    findSameIngredientElsewhere(
        templateName: string,
        sourceItemId: string,
        candidate: SwapCandidate,
        appliedQuantity: number
    ): MatchingIngredientRef[];
    /**
     * Which swap tabs this flow offers. The subrecipe import is deliberately
     * nesting-free, so it omits 'subrecipe'.
     */
    allowedSwapKinds: SwapCandidate['kind'][];
}

/** The unit a candidate's quantity is expressed in (std unit, or portions for subrecipes). */
export function candidateUnit(candidate: SwapCandidate): string | undefined {
    return candidate.quantityInfo?.stdUnit ?? candidate.portionsUnit;
}

/** Same-ingredient comparison: the automation reuses one template name verbatim across items. */
export function sameTemplateName(a: string, b: string): boolean {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** True when a report ingredient already points at `candidate` — nothing to apply. */
export function alreadyOnCandidate(ingredient: ReviewIngredient, candidate: SwapCandidate): boolean {
    if (candidate.kind === 'subrecipe') return ingredient.subrecipeId === candidate.subrecipeId;
    return !!candidate.articleId
        && ingredient.articleId === candidate.articleId
        && ingredient.platform === candidate.platform;
}

export type SwapReviewAdapter = Pick<ReviewWriteAdapter, 'swap' | 'findSameIngredientElsewhere' | 'allowedSwapKinds'>;
